import { ComponentProps, useEffect, useMemo, useState } from 'react'
import { CircleHelp, Trash2 } from 'lucide-react'
import { imageUrl } from 'app/frontend/base/helpers'
import { raidMarkerTypes } from 'app/frontend/types/custom/RaidMarker'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AssignmentBoxConfigurator from './AssignmentBoxConfigurator'
import AssignmentEntityChip from './AssignmentEntityChip'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { Separator } from '@/components/ui/separator'
import useCastNoteDraft from '../hooks/useCastNoteDraft'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import { buildAssignmentPaletteEntities } from './assignmentPaletteEntities'
import { getTimelineSpellNameById } from 'app/frontend/base/services/raidPlanData'
import { formatTimelineTimestamp } from './timelineMath'
import {
  TeamPlanTimelineCastEditorCast,
  TeamPlanTimelineCastEditorDraft,
} from './timelineCast'
import { Badge } from '@/components/ui/badge'
import { FormGroup } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import Card from 'app/frontend/base/components/Card'

type CastSettingsDialogProps = {
  open: boolean
  cast: TeamPlanTimelineCastEditorCast | null
  allCasts: TeamPlanTimelineCastEditorCast[]
  onSave: (castId: string, next: TeamPlanTimelineCastEditorDraft) => void
  onRemoveCast: (castId: string) => void
  onOpenChange: (open: boolean) => void
}

const CastSettingsDialog = ({
  open,
  cast,
  allCasts,
  onSave,
  onRemoveCast,
  onOpenChange,
}: CastSettingsDialogProps) => {
  const { t } = useTranslation()
  const { computed, rosterMemberships } = useTeamPlanTimeline()
  const {
    draft: messageDraft,
    setDraft: setMessageDraft,
    inputRef: messageInputRef,
    insertMarker: insertMarkerToMessage,
  } = useCastNoteDraft()
  const [ttsMessageDraft, setTtsMessageDraft] = useState('')
  const [rgbDraft, setRgbDraft] = useState('')
  const [glowTargetsOnRaidFramesDraft, setGlowTargetsOnRaidFramesDraft] =
    useState(true)
  const [addTargetsToReminderTextDraft, setAddTargetsToReminderTextDraft] =
    useState(true)
  const [targetEntities, setTargetEntities] = useState<
    RaidPlanAssignmentEntity[]
  >([])
  const specializationById = useMemo(
    () =>
      computed.specializations.reduce<
        Record<
          number,
          {
            name: string
            icon: string
            classId: number
            className: string
          }
        >
      >((acc, specialization) => {
        acc[specialization.id] = {
          name: specialization.name,
          icon: specialization.icon,
          classId: specialization.classId,
          className: specialization.className,
        }
        return acc
      }, {}),
    [computed.specializations],
  )
  const paletteEntities = useMemo<
    ComponentProps<typeof AssignmentBoxConfigurator>['paletteEntities']
  >(
    () =>
      buildAssignmentPaletteEntities({
        t,
        members: rosterMemberships,
        specializationById,
        classColor: computed.classColor,
      }),
    [computed.classColor, specializationById, t, rosterMemberships],
  )
  const assignmentUsageCountByIdentity = useMemo(() => {
    return allCasts.reduce<Record<string, number>>((acc, currentCast) => {
      ;(currentCast.targets ?? []).forEach((group) => {
        group.forEach((entity) => {
          const identity = getAssignmentEntityIdentity(entity)
          acc[identity] = (acc[identity] ?? 0) + 1
        })
      })
      return acc
    }, {})
  }, [allCasts])
  const commit = (
    castId?: string | null,
    options?: {
      message?: string
      ttsMessage?: string
      rgb?: string
      targetEntities?: RaidPlanAssignmentEntity[]
      glowTargetsOnRaidFrames?: boolean
      addTargetsToReminderText?: boolean
    },
  ) => {
    if (!castId) return
    const nextMessage = (options?.message ?? messageDraft).trim()
    const nextTtsMessage = (options?.ttsMessage ?? ttsMessageDraft).trim()
    const nextRgb = (options?.rgb ?? rgbDraft).trim()
    const nextGlowTargetsOnRaidFrames =
      options?.glowTargetsOnRaidFrames ?? glowTargetsOnRaidFramesDraft
    const nextAddTargetsToReminderText =
      options?.addTargetsToReminderText ?? addTargetsToReminderTextDraft
    const nextTargets = (options?.targetEntities ?? targetEntities).filter(
      Boolean,
    )
    onSave(castId, {
      message: nextMessage.length ? nextMessage : undefined,
      ttsMessage: nextTtsMessage.length ? nextTtsMessage : undefined,
      rgb: nextRgb.length ? nextRgb : undefined,
      targets: nextTargets.length ? [nextTargets] : undefined,
      glowTargetsOnRaidFrames: nextGlowTargetsOnRaidFrames ? undefined : false,
      addTargetsToReminderText: nextAddTargetsToReminderText
        ? undefined
        : false,
    })
  }
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      commit(cast?.id)
    }
    onOpenChange(nextOpen)
  }
  const handleTargetsSave = (
    nextTargetsByGroup: RaidPlanAssignmentEntity[][],
  ) => {
    const nextTargetEntities = nextTargetsByGroup.flat().filter(Boolean)
    setTargetEntities(nextTargetEntities)
    commit(cast?.id, { targetEntities: nextTargetEntities })
  }

  useEffect(() => {
    if (!cast) return
    setMessageDraft(cast.message ?? '')
    setTtsMessageDraft(cast.ttsMessage ?? '')
    setRgbDraft(cast.rgb ?? '')
    setGlowTargetsOnRaidFramesDraft(cast.glowTargetsOnRaidFrames !== false)
    setAddTargetsToReminderTextDraft(cast.addTargetsToReminderText !== false)
    setTargetEntities((cast.targets ?? []).flat().filter(Boolean))
  }, [cast?.id])

  const name = cast?.spellId
    ? getTimelineSpellNameById()[cast.spellId] ?? `Spell ${cast.spellId}`
    : 'Message'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='w-[44rem] max-w-[44rem] p-3 gap-3'>
        <DialogTitle className='flex items-center gap-2'>
          <span>{name}</span>
          <Badge variant='outline'>
            {formatTimelineTimestamp(cast?.at ?? 0)}
          </Badge>
        </DialogTitle>

        <FormGroup>
          <div className='grow'>
            <Label>{t('dashboard.team.plans.assignments.note-options')}</Label>
            <Input
              ref={messageInputRef}
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              onBlur={() => commit(cast?.id)}
              placeholder={name}
            />
            <div className='flex items-center gap-1.5 mt-2'>
              {raidMarkerTypes.map((marker) => (
                <Button
                  key={marker}
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => insertMarkerToMessage(marker)}
                >
                  <img
                    src={imageUrl(`markers/${marker}.png`)}
                    className='h-4 w-4 min-w-4 min-h-4'
                  />
                </Button>
              ))}
            </div>
          </div>

          <div className='grow'>
            <Label>
              {t('dashboard.team.plans.assignments.note-tts-options')}
            </Label>
            <Input
              value={ttsMessageDraft}
              onChange={(event) => setTtsMessageDraft(event.target.value)}
              onBlur={() => commit(cast?.id)}
              placeholder={messageDraft || name}
            />
          </div>

          <div>
            <Label>Color</Label>
            <ColorPicker
              value={rgbDraft || 'rgb(255, 255, 255)'}
              format='rgb'
              onValueChange={setRgbDraft}
              onOpenChange={(open) => {
                if (open) return
                commit(cast?.id)
              }}
            >
              <ColorPickerTrigger asChild>
                <Button variant='ghost' className='p-0 h-auto'>
                  <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-18.5 h-9' />
                </Button>
              </ColorPickerTrigger>
              <ColorPickerContent>
                <ColorPickerArea />
                <ColorPickerHueSlider />
                <ColorPickerInput />
              </ColorPickerContent>
            </ColorPicker>
          </div>
        </FormGroup>

        <Separator />

        <div>
          <Label>Targets</Label>
          <AssignmentBoxConfigurator
            spellId={cast?.spellId ?? 0}
            entitiesByGroup={[targetEntities]}
            paletteEntities={paletteEntities}
            assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
            canEdit
            onSave={handleTargetsSave}
            onOpenChange={() => null}
            trigger={
              <Card className='flex flex-wrap items-center gap-1 h-10 py-1 px-3 cursor-pointer'>
                {targetEntities.map((entity, entityIndex) => (
                  <AssignmentEntityChip
                    key={`${getAssignmentEntityIdentity(
                      entity,
                    )}-${entityIndex}`}
                    index={entityIndex}
                    label={getAssignmentEntityLabel(t, entity)}
                  />
                ))}

                {targetEntities.length === 0 ? (
                  <span className='text-muted-foreground'>
                    No targets assigned
                  </span>
                ) : null}
              </Card>
            }
          />
        </div>

        <FormGroup className='gap-6'>
          <div className='flex items-center gap-2'>
            <Checkbox
              id='glow-targets-on-raid-frames'
              checked={glowTargetsOnRaidFramesDraft}
              onCheckedChange={(checked) => {
                const nextValue = checked === true
                setGlowTargetsOnRaidFramesDraft(nextValue)
                commit(cast?.id, {
                  glowTargetsOnRaidFrames: nextValue,
                })
              }}
            />
            <Label
              htmlFor='glow-targets-on-raid-frames'
              className='inline-flex items-center gap-1.5'
            >
              Glow on raid frames
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex items-center text-muted-foreground'>
                    <CircleHelp className='h-3.5 w-3.5' />
                  </span>
                </TooltipTrigger>
                <TooltipContent className='max-w-72'>
                  This will add a glow to any character with the names of the
                  targets, disregarding the realm.
                </TooltipContent>
              </Tooltip>
            </Label>
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox
              id='add-targets-to-reminder-text'
              checked={addTargetsToReminderTextDraft}
              onCheckedChange={(checked) => {
                const nextValue = checked === true
                setAddTargetsToReminderTextDraft(nextValue)
                commit(cast?.id, {
                  addTargetsToReminderText: nextValue,
                })
              }}
            />
            <Label htmlFor='add-targets-to-reminder-text'>
              Add to reminder text
            </Label>
          </div>
        </FormGroup>

        <div className='flex justify-end gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => {
              if (!cast?.id) return
              onRemoveCast(cast.id)
              onOpenChange(false)
            }}
          >
            <Trash2 className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => handleOpenChange(false)}
          >
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CastSettingsDialog
